import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { storage } from "./storage";

/**
 * Global axios response interceptor (audit C5.5):
 *  - On 401, attempts to refresh the JWT via POST /auth/refresh using the
 *    stored refresh_token, then retries the original request once.
 *  - Concurrent 401s share a single in-flight refresh promise to avoid a
 *    burst of refresh calls when several requests fail in parallel.
 *  - If refresh fails, clears auth state and sends the user to /get-started.
 *
 * Hooks keep calling `axios.post(...)` directly — the interceptor is on the
 * default instance, so no per-hook changes are needed.
 */

const REFRESH_ENDPOINT = "/auth/refresh";

const baseUrl =
  process.env.REACT_APP_API_CONFIG ||
  (typeof window !== "undefined"
    ? `${window.location.origin}/api`
    : "");

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshInFlight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = storage.getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }
  // Use raw axios.post (no interceptor for refresh itself to avoid loops).
  const res = await axios.post(
    `${baseUrl}${REFRESH_ENDPOINT}`,
    { refresh_token: refreshToken },
    { headers: { "Content-Type": "application/json" } }
  );
  const newJwt = res.data?.jwt;
  const newRefresh = res.data?.refresh_token;
  if (!newJwt || !newRefresh) {
    throw new Error("Refresh response missing tokens");
  }
  storage.setJwtToken(newJwt);
  storage.setRefreshToken(newRefresh);
  return newJwt;
}

function redirectToLogin(): void {
  storage.removeItem("JWT_TOKEN");
  storage.clearRefreshToken();
  if (
    typeof window !== "undefined" &&
    !window.location.pathname.startsWith("/get-started") &&
    !window.location.pathname.startsWith("/confirm")
  ) {
    window.location.href = "/get-started";
  }
}

axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableRequest | undefined;
    const status = error.response?.status;

    // Bail if not a retryable 401 — also skip the refresh endpoint itself
    // (otherwise a failing refresh would recurse forever).
    if (
      !original ||
      status !== 401 ||
      original._retry ||
      original.url?.includes(REFRESH_ENDPOINT)
    ) {
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      if (!refreshInFlight) {
        refreshInFlight = performRefresh().finally(() => {
          refreshInFlight = null;
        });
      }
      const newJwt = await refreshInFlight;
      if (original.headers) {
        original.headers.Authorization = `Bearer ${newJwt}`;
      }
      return axios(original);
    } catch (refreshError) {
      redirectToLogin();
      return Promise.reject(refreshError);
    }
  }
);

export {};
